import type { Plugin } from 'chart.js';

export interface DataLabelPluginOptions {
    isDarkTheme: boolean;
    displayType: 'value' | 'percentage';
    precision?: number;
    fontSize?: string;
    fontFamily?: string;
}

export const createDataLabelsPlugin = (
    options: DataLabelPluginOptions,
): Plugin<'bar'> => {
    const {
        isDarkTheme,
        displayType,
        precision = 3,
        fontSize = '12px',
        fontFamily = 'Arial',
    } = options;

    return {
        id: 'dataLabels',
        afterDatasetsDraw(chart) {
            const { ctx, data } = chart;
            const { datasets } = data;

            // Calculate total for percentage display if needed
            let totalSum = 0;

            if (displayType === 'percentage') {
                datasets.forEach((dataset) => {
                    dataset.data.forEach((value) => {
                        if (typeof value === 'number' && value > 0) {
                            totalSum += value;
                        }
                    });
                });
            }

            ctx.save();
            ctx.font = `${fontSize} ${fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);

                if (!meta.hidden) {
                    meta.data.forEach((element, index) => {
                        const value = dataset.data[index] as number;

                        if (
                            value !== null &&
                            value !== undefined &&
                            value > 0
                        ) {
                            const { x, y } = element.getProps(['x', 'y']);

                            ctx.fillStyle = isDarkTheme ? '#ffffff' : '#000000';
                            ctx.strokeStyle = isDarkTheme
                                ? '#000000'
                                : '#ffffff';
                            ctx.lineWidth = 1;

                            let displayValue: string;

                            if (displayType === 'percentage' && totalSum > 0) {
                                const percentage = (value / totalSum) * 100;

                                displayValue = `${percentage.toFixed(1)}%`;
                            } else {
                                displayValue = value.toFixed(precision);
                            }

                            ctx.strokeText(displayValue, x, y);
                            ctx.fillText(displayValue, x, y);
                        }
                    });
                }
            });

            ctx.restore();
        },
    };
};
